import { useId } from 'react';
import { SegmentedControl } from '../common/SegmentedControl';

/** Where a template task falls against its event's date. */
export type OffsetDirection = 'NONE' | 'BEFORE' | 'ON' | 'AFTER';

export interface OffsetDraft {
  direction: OffsetDirection;
  /** The whole days typed, before or after — unused on the day or with no date. */
  days: string;
}

/** The longest a template reaches from its event: ten years either way. */
export const MAX_OFFSET_DAYS = 3650;

const DIRECTIONS: { id: OffsetDirection; label: string }[] = [
  { id: 'NONE', label: 'No date' },
  { id: 'BEFORE', label: 'Before' },
  { id: 'ON', label: 'Event day' },
  { id: 'AFTER', label: 'After' },
];

/** A stored offset (negative = before the event) as the field shows it. */
export function offsetToDraft(offset: number | null | undefined): OffsetDraft {
  if (offset == null) return { direction: 'NONE', days: '' };
  if (offset === 0) return { direction: 'ON', days: '' };
  return { direction: offset < 0 ? 'BEFORE' : 'AFTER', days: String(Math.abs(offset)) };
}

/**
 * The offset to store, `null` for no date, or `undefined` while the days typed
 * are not a whole number from 1 to {@link MAX_OFFSET_DAYS} — never rounded,
 * since a task landing on a day other than the one typed is worse than being
 * told the number will not do.
 */
export function draftToOffset(draft: OffsetDraft): number | null | undefined {
  if (draft.direction === 'NONE') return null;
  if (draft.direction === 'ON') return 0;
  const text = draft.days.trim();
  if (!/^\d+$/.test(text)) return undefined;
  const n = Number(text);
  if (n < 1 || n > MAX_OFFSET_DAYS) return undefined;
  return draft.direction === 'BEFORE' ? -n : n;
}

export interface EventOffsetFieldProps {
  value: OffsetDraft;
  onChange: (value: OffsetDraft) => void;
}

/**
 * When a template task is due, counted from the event's date: "14 days before",
 * "on the event day", "3 days after". Copying the template onto an event turns
 * it into a real due date.
 */
export function EventOffsetField({ value, onChange }: EventOffsetFieldProps) {
  const counted = value.direction === 'BEFORE' || value.direction === 'AFTER';
  const invalid = counted && draftToOffset(value) === undefined;
  const hintId = useId();
  return (
    <div>
      <span className="block text-xs font-medium text-gray-600 mb-1">Due</span>
      <div className="flex flex-wrap items-center gap-2">
        {counted && (
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_OFFSET_DAYS}
            aria-label="Days from the event"
            aria-invalid={invalid || undefined}
            aria-describedby={hintId}
            value={value.days}
            onChange={(e) => onChange({ ...value, days: e.target.value })}
            className={`w-20 min-h-11 sm:min-h-0 rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              invalid ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        )}
        {counted && <span className="text-sm text-gray-600">days</span>}
        <SegmentedControl
          ariaLabel="Due against the event"
          options={DIRECTIONS}
          value={value.direction}
          onChange={(direction) =>
            onChange({
              direction,
              // Switching between before and after keeps the number typed.
              days:
                direction === 'BEFORE' || direction === 'AFTER' ? value.days || '7' : value.days,
            })
          }
        />
        {counted && <span className="text-sm text-gray-600">the event</span>}
      </div>
      {invalid ? (
        <p id={hintId} role="alert" className="mt-1 text-xs text-red-600">
          Enter a whole number of days from 1 to {MAX_OFFSET_DAYS}.
        </p>
      ) : (
        <p id={hintId} className="mt-1 text-xs text-gray-500">
          Set from the event&apos;s date when this template is copied onto an event.
        </p>
      )}
    </div>
  );
}
